import React from 'react';
import {
  humanize,
  isTaxonomyValueLeak,
  legacyNumericTargetForRow,
  normalizeValue,
  suggestedCanonicalForRow,
} from './taxonomyLibrarianUtils';


export default function TaxonomyReviewActions({ row, actions }) {
  const {
    cleaningQueueId,
    reviewingQueueId,
    approvedTermsForRow,
    approvedFacetsForRow,
    sourceValueForRow,
    approveTerm,
    retireTerm,
    setFacetForTerm,
    cleanTermLabel,
    ignoreReviewRow,
    applyTaxonomyRewrite,
    applyLocationRewrite,
    rewriteLocationLeak,
    rewriteTaxonomyRow,
    mergeIntoExistingTerm,
    mergeAlias,
    rejectAlias,
    startServicesSplitPass,
    migrateToRetrievalLane,
  } = actions;

  const actionButtonClass = 'mt-2 inline-flex h-8 items-center rounded-md border px-2.5 text-xs font-medium disabled:opacity-50';
  const busyRow = row => reviewingQueueId === row.queue_id || cleaningQueueId === row.queue_id;
  const ignoreButton = (row, label = 'Ignore') => (
    <button
      type="button"
      onClick={() => ignoreReviewRow(row)}
      disabled={busyRow(row)}
      className={`${actionButtonClass} border-gray-200 bg-white text-gray-500 hover:bg-gray-50`}
    >
      {label}
    </button>
  );
  const retireButton = (row, label = 'Retire') => (
    <button
      type="button"
      onClick={() => retireTerm(row)}
      disabled={busyRow(row)}
      className={`${actionButtonClass} border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}
    >
      {reviewingQueueId === row.queue_id ? 'Working...' : label}
    </button>
  );
  const mergeSelect = row => {
    const targets = approvedTermsForRow(row);
    if (!targets.length) return null;
    return (
      <select
        value=""
        onChange={event => mergeIntoExistingTerm(row, event.target.value)}
        disabled={busyRow(row)}
        className="mt-2 h-8 max-w-48 rounded-md border border-blue-200 bg-blue-50 px-2 text-xs font-medium text-blue-700 disabled:opacity-50"
        aria-label={`Merge ${row.tag} into existing term`}
      >
        <option value="">Merge into existing...</option>
        {targets.map(term => (
          <option key={term.id || term.value} value={term.value}>{term.label || term.value}</option>
        ))}
      </select>
    );
  };
  const facetSelect = row => {
    const facets = approvedFacetsForRow(row);
    if (!facets.length) return null;
    return (
      <select
        value=""
        onChange={event => event.target.value && setFacetForTerm(row, event.target.value)}
        disabled={busyRow(row)}
        className="mt-2 h-8 rounded-md border border-blue-200 bg-blue-50 px-2 text-xs font-medium text-blue-700 disabled:opacity-50"
        aria-label={`Set facet for ${row.tag}`}
      >
        <option value="">Choose approved facet...</option>
        {facets.map(facet => (
          <option key={facet.id || facet.value} value={facet.value}>{facet.label || humanize(facet.value)}</option>
        ))}
      </select>
    );
  };

  const renderReviewActions = row => {
    const disabled = busyRow(row);
    if (row.review_type === 'alias_candidate') {
      return (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => mergeAlias(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
          >
            {reviewingQueueId === row.queue_id ? 'Working...' : 'Merge'}
          </button>
          <button
            type="button"
            onClick={() => rejectAlias(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}
          >
            Reject alias
          </button>
          {retireButton(row, 'Retire term')}
          {ignoreButton(row)}
        </div>
      );
    }
    if (normalizeValue(row.recommended_action) === 'migrate_to_retrieval_lane') {
      return (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => migrateToRetrievalLane(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
          >
            {reviewingQueueId === row.queue_id ? 'Working...' : 'Move to lane'}
          </button>
          {ignoreButton(row)}
        </div>
      );
    }
    if (normalizeValue(row.recommended_action) === 'split_guest_vs_local') {
      return (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => startServicesSplitPass(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100`}
          >
            Classify place lane
          </button>
          {ignoreButton(row)}
        </div>
      );
    }
    if (normalizeValue(row.issue_type) === 'card_tag_structured_duplicate') {
      return (
        <div className="flex flex-wrap gap-1">
          {retireButton(row, 'Retire tag')}
          {ignoreButton(row)}
        </div>
      );
    }
    const legacyTarget = legacyNumericTargetForRow(row);
    if (legacyTarget) {
      return (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => applyTaxonomyRewrite(row, legacyTarget, { sourceValue: row.tag || row.raw_value })}
            disabled={disabled}
            className={`${actionButtonClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
          >
            {reviewingQueueId === row.queue_id ? 'Working...' : `Apply: ${legacyTarget}`}
          </button>
          <button
            type="button"
            onClick={() => rewriteTaxonomyRow(row, legacyTarget)}
            disabled={disabled}
            className={`${actionButtonClass} border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}
          >
            Rewrite to...
          </button>
          {mergeSelect(row)}
          {retireButton(row)}
          {ignoreButton(row)}
        </div>
      );
    }
    if (row.issue_type === 'pending_term_suggestion') {
      return (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => approveTerm(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
          >
            {reviewingQueueId === row.queue_id ? 'Working...' : 'Approve'}
          </button>
          <button
            type="button"
            onClick={() => retireTerm(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}
          >
            Retire
          </button>
          <button
            type="button"
            onClick={() => rewriteTaxonomyRow(row, row.canonical_tag || row.tag)}
            disabled={disabled}
            className={`${actionButtonClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
          >
            Rewrite to...
          </button>
          {mergeSelect(row)}
          {ignoreButton(row)}
        </div>
      );
    }
    if (row.issue_type === 'used_not_in_approved_vocabulary') {
      return (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => approveTerm(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
          >
            {reviewingQueueId === row.queue_id ? 'Working...' : 'Approve tag'}
          </button>
          <button
            type="button"
            onClick={() => rewriteTaxonomyRow(row, row.canonical_tag || row.tag)}
            disabled={disabled}
            className={`${actionButtonClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
          >
            Rewrite to...
          </button>
          {mergeSelect(row)}
          {retireButton(row)}
          {ignoreButton(row)}
        </div>
      );
    }
    if (row.issue_type === 'missing_facet') {
      return (
        <div className="flex flex-wrap gap-1">
          {facetSelect(row)}
          <button
            type="button"
            onClick={() => setFacetForTerm(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
          >
            {reviewingQueueId === row.queue_id ? 'Working...' : 'Propose facet'}
          </button>
          {retireButton(row)}
          {ignoreButton(row)}
        </div>
      );
    }
    if (row.issue_type === 'non_normalized_term') {
      return (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => applyTaxonomyRewrite(row, row.tag, { allowSameNormalized: true })}
            disabled={disabled}
            className={`${actionButtonClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
          >
            {reviewingQueueId === row.queue_id ? 'Working...' : `Normalize to ${row.tag}`}
          </button>
          <button
            type="button"
            onClick={() => rewriteTaxonomyRow(row, row.tag)}
            disabled={disabled}
            className={`${actionButtonClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
          >
            Rewrite to...
          </button>
          {mergeSelect(row)}
          <button
            type="button"
            onClick={() => retireTerm(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}
          >
            Retire bad value
          </button>
          {ignoreButton(row)}
        </div>
      );
    }
    if (row.issue_type === 'label_contains_hash') {
      return (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => cleanTermLabel(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
          >
            {reviewingQueueId === row.queue_id ? 'Working...' : 'Clean label'}
          </button>
          {retireButton(row)}
          {ignoreButton(row)}
        </div>
      );
    }
    if (isTaxonomyValueLeak(row)) {
      return (
        <div className="flex flex-wrap gap-1">
          {row.canonical_tag && (
            <button
              type="button"
              onClick={() => applyLocationRewrite(row, row.canonical_tag)}
              disabled={disabled}
              className={`${actionButtonClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
            >
              {reviewingQueueId === row.queue_id ? 'Working...' : `Apply: ${row.canonical_tag}`}
            </button>
          )}
          <button
            type="button"
            onClick={() => rewriteLocationLeak(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
          >
            Rewrite to...
          </button>
          {mergeSelect(row)}
          <button
            type="button"
            onClick={() => retireTerm(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}
          >
            Retire
          </button>
          {ignoreButton(row)}
        </div>
      );
    }
    if (row.issue_type === 'approved_term_unused') {
      return (
        <div className="flex flex-wrap gap-1">
          {ignoreButton(row, 'Keep')}
          <button
            type="button"
            onClick={() => retireTerm(row)}
            disabled={disabled}
            className={`${actionButtonClass} border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}
          >
            {reviewingQueueId === row.queue_id ? 'Working...' : 'Retire'}
          </button>
        </div>
      );
    }
    const issueType = normalizeValue(row.issue_type);
    if (
      ['retired_term_still_used', 'raw_values_collapse_to_same_tag', 'non_normalized_usage'].includes(issueType)
      && row.recommended_action !== 'remove_literal_yes_from_current_rows'
    ) {
      const normalizeAction = issueType !== 'retired_term_still_used';
      const inferredCanonicalTag = suggestedCanonicalForRow(row);
      const suggestedTarget = !normalizeAction && inferredCanonicalTag;
      const sourceValue = normalizeValue(row.tag || row.raw_value || sourceValueForRow(row));
      return (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => (
              normalizeAction
                ? applyTaxonomyRewrite(row, row.tag, { sourceValue: row.tag, allowSameNormalized: true })
                : suggestedTarget
                  ? applyTaxonomyRewrite(row, inferredCanonicalTag, { sourceValue })
                  : rewriteTaxonomyRow(row, inferredCanonicalTag || row.canonical_tag || row.tag)
            )}
            disabled={disabled}
            className={`${actionButtonClass} ${suggestedTarget ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
          >
            {normalizeAction ? 'Normalize' : suggestedTarget ? `Apply: ${inferredCanonicalTag}` : 'Rewrite to...'}
          </button>
          {(normalizeAction || suggestedTarget) && (
            <button
              type="button"
              onClick={() => rewriteTaxonomyRow(row, inferredCanonicalTag || row.canonical_tag || row.tag)}
              disabled={disabled}
              className={`${actionButtonClass} border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}
            >
              Rewrite to...
            </button>
          )}
          {mergeSelect(row)}
          {retireButton(row)}
          {ignoreButton(row)}
        </div>
      );
    }
    return null;
  };

  return renderReviewActions(row);
}
